import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Check, CaretRight, Sparkle } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getProgramConfig, getProgramSubtitleKey, getProgramDescKey, CHIP_TRANSLATION_MAP, NANO_WAX_BULLET_KEYS, LUXURY_INTERIOR_COMBO_DISCOUNT } from '../config/programs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtLek = (n) => Number(n).toLocaleString('de-DE');

// Luxury-only highlighted block: NANO WAX — efekt pasqyre ekstra ✨ + 3 sub-bullets.
function NanoWaxBlock({ programId, t }) {
  return (
    <div
      className="mt-3 rounded-2xl border border-[#FFD700]/35 bg-gradient-to-br from-[#FFD700]/15 to-[#FF5A00]/10 p-3"
      data-testid={`nano-wax-block-${programId}`}
    >
      <p className="text-[12px] font-extrabold tracking-wide text-[#FFD700]">{t('chipNanoWax')}</p>
      <ul className="mt-1.5 space-y-1">
        {NANO_WAX_BULLET_KEYS.map((k) => (
          <li key={k} className="text-[11px] text-white/85 flex items-start gap-1.5" data-testid={`${k}-${programId}`}>
            <Check size={12} weight="bold" className="text-[#FFD700] mt-0.5 shrink-0" />
            <span>{t(k)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// "Luxury + Pastrim i brendshëm": full price struck through, combo price, 07:00–19:00.
function LuxuryComboLine({ program, interiorExtra, t }) {
  if (!interiorExtra) return null;
  const full = program.price + interiorExtra.price;
  const combo = full - LUXURY_INTERIOR_COMBO_DISCOUNT;
  return (
    <div
      className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2"
      data-testid={`luxury-combo-${program.program_id}`}
    >
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-white truncate">{t('luxuryComboLabel')}</p>
        <p className="text-[10px] text-white/45">{t('luxuryComboHours')}</p>
      </div>
      <p className="text-sm font-bold text-white whitespace-nowrap">
        <s className="text-white/40 font-medium mr-1.5">{fmtLek(full)}</s>
        <span className="text-[#FFD700]">{fmtLek(combo)} {t('lek')}</span>
      </p>
    </div>
  );
}

export default function ProgramsPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [extras, setExtras] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedCar, setSelectedCar] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progRes, extrasRes, carsRes] = await Promise.all([
          axios.get(`${API}/wash-programs`),
          axios.get(`${API}/extra-services`),
          axios.get(`${API}/cars`, { withCredentials: true })
        ]);
        setPrograms(progRes.data);
        setExtras(extrasRes.data);
        setCars(carsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSelectProgram = (program) => {
    setSelectedProgram(program);
    setSelectedExtras([]);
    setShowDialog(true);
  };

  const toggleExtra = (serviceId) => {
    setSelectedExtras(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateTotal = () => {
    let total = selectedProgram?.price || 0;
    selectedExtras.forEach(id => {
      const extra = extras.find(e => e.service_id === id);
      if (extra) total += extra.price;
    });
    return total;
  };

  const handlePurchase = async () => {
    if (!selectedCar) {
      toast.error(t('pleaseSelectCar'));
      return;
    }

    setPurchasing(true);
    try {
      await axios.post(`${API}/washes/purchase`, {
        wash_program_id: selectedProgram.program_id,
        car_id: selectedCar,
        extras: selectedExtras
      }, { withCredentials: true });

      toast.success(t('success') + '!');
      setShowDialog(false);
      navigate('/history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purchase');
    } finally {
      setPurchasing(false);
    }
  };

  // Sort programs by price; "Nano Wax" is rendered as a block, not a chip.
  const sortedPrograms = useMemo(
    () => [...programs].sort((a, b) => a.price - b.price)
      .map((p) => ({ ...p, chipFeatures: (p.features || []).filter((f) => f !== 'Nano Wax') })),
    [programs],
  );
  const interiorBase = useMemo(() => extras.find((e) => e.service_id === 'extra_interior_staff'), [extras]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] safe-bottom flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#0ABAB5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] safe-bottom">
      {/* Header */}
      <div className="p-4 pt-6">
        <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']">{t('washPrograms')}</h1>
        <p className="text-white/50 mt-1">{t('selectProgram')}</p>
      </div>

      {/* Programs List */}
      <div className="px-4 space-y-4 pb-8">
        {sortedPrograms.map((program, index) => {
          const config = getProgramConfig(program.name_en);
          const isStandard = program.name_en === 'Standard';
          const isLuxury = program.name_en === 'Luxury';
          const isPremiumTier = isStandard || isLuxury;

          return (
            <motion.div
              key={program.program_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.08 }}
            >
              <button
                onClick={() => handleSelectProgram(program)}
                className={`w-full text-left rounded-3xl border overflow-hidden transition-all duration-300 hover:scale-[1.01] active:scale-[0.99] relative ${
                  isStandard
                    ? 'border-2 shadow-[0_0_30px_rgba(255,90,0,0.25)]'
                    : isLuxury
                    ? 'border-2 shadow-[0_0_24px_rgba(239,68,68,0.18)]'
                    : 'border border-white/5 hover:border-white/10'
                }`}
                style={{
                  borderColor: isPremiumTier ? `${config.color}50` : undefined,
                  background: isPremiumTier 
                    ? `linear-gradient(135deg, ${config.colorDark} 0%, #0a0a0a 100%)`
                    : '#121212'
                }}
                data-testid={`program-${program.program_id}`}
              >
                {/* Most Popular TOP BANNER — Iter18 Task 1. Crown emoji + text. */}
                {isStandard && (
                  <div
                    className="relative w-full px-3 py-2 flex items-center justify-center gap-1.5 bg-gradient-to-r from-[#FF5A00] via-[#FF7A2C] to-[#FF5A00]"
                    data-testid={`badge-most-popular-${program.program_id}`}
                  >
                    <span className="text-base leading-none" role="img" aria-label="crown">👑</span>
                    <span className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-white drop-shadow-sm">
                      {t('mostPopular')}
                    </span>
                  </div>
                )}

                {/* Luxury Premium Badge */}
                {isLuxury && (
                  <div 
                    className="px-4 py-1.5 text-center text-sm font-bold flex items-center justify-center gap-2"
                    style={{ backgroundColor: config.color, color: 'white' }}
                  >
                    <Sparkle size={16} weight="fill" />
                    PREMIUM
                    <Sparkle size={16} weight="fill" />
                  </div>
                )}

                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Car Image — Iter18 Task 1: all programs use config.carImage (Standard
                        uses Fast_3.png — the official orange AutoSpa5 car). The CSS scale-125
                        on the img inflates the car visually to fill the slot. */}
                    <div 
                      className="w-24 h-20 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: config.colorDark }}
                    >
                      <img 
                        src={config.carImage} 
                        alt={program.name_en}
                        className="w-full h-full object-contain scale-125"
                        loading="lazy"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 
                            className="text-xl font-semibold font-['Outfit']"
                            style={{ color: config.color }}
                          >
                            {program.name_en}
                          </h3>
                          <p className="text-white/60 text-sm">{t(getProgramSubtitleKey(program.name_en))}</p>
                        </div>
                        <div className="text-right">
                          <p 
                            className="text-2xl font-bold"
                            style={{ color: config.color }}
                          >
                            {program.price}
                          </p>
                          <p className="text-white/40 text-xs">{t('lek')}</p>
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="flex items-center gap-1.5 mt-2 text-white/50 text-sm">
                        <Clock size={14} />
                        <span>{program.duration_minutes} {t('minutes')}</span>
                      </div>

                      {/* Features — "Nano Wax" is rendered as a highlighted block (Luxury only) */}
                      {program.chipFeatures.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {program.chipFeatures.map((feature, i) => {
                            const chipKey = CHIP_TRANSLATION_MAP[feature];
                            const label = chipKey ? t(chipKey) : feature;
                            const isWarning = feature === 'No drying';
                            const isGold = feature === 'Tire shine' || feature === 'Protective candle wax';
                            return (
                              <span 
                                key={feature} 
                                data-testid={`chip-${program.program_id}-${i}`}
                                className={`px-2 py-0.5 text-xs rounded-md ${isGold ? 'font-semibold' : ''}`}
                                style={isWarning
                                  ? { backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B', fontWeight: 600 }
                                  : isGold
                                    ? { backgroundColor: 'rgba(255, 215, 0, 0.15)', color: '#FFD700' }
                                    : { backgroundColor: config.colorLight, color: config.color }}
                              >
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {program.features?.includes('Nano Wax') && <NanoWaxBlock programId={program.program_id} t={t} />}
                      {isLuxury && <LuxuryComboLine program={program} interiorExtra={interiorBase} t={t} />}
                    </div>
                  </div>
                </div>
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Purchase Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-md mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">
              {selectedProgram?.name_en}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {selectedProgram && t(getProgramDescKey(selectedProgram.name_en))}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Car Selection */}
            <div>
              <label className="text-sm text-white/60 mb-2 block">{t('selectCar')}</label>
              {cars.length === 0 ? (
                <Button
                  variant="outline"
                  onClick={() => navigate('/my-car')}
                  className="w-full h-12 border-white/10 bg-white/5"
                >
                  {t('addCar')} <CaretRight size={16} className="ml-2" />
                </Button>
              ) : (
                <div className="space-y-2">
                  {cars.map(car => (
                    <button
                      key={car.car_id}
                      onClick={() => setSelectedCar(car.car_id)}
                      className={`w-full p-3 rounded-xl border text-left transition-all ${
                        selectedCar === car.car_id 
                          ? 'border-[#0ABAB5] bg-[#0ABAB5]/10' 
                          : 'border-white/10 bg-white/5'
                      }`}
                    >
                      <span className="font-mono font-medium">{car.license_plate}</span>
                      {car.make && <span className="text-white/50 ml-2">{car.make} {car.model}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Extra Services REMOVED for customers — handled at the physical Awoara terminal */}

            {/* Total */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex justify-between items-center mb-4">
                <span className="text-white/60">Total</span>
                <span className="text-3xl font-bold text-[#0ABAB5]">{calculateTotal()} {t('lek')}</span>
              </div>

              {selectedProgram && (
                <p className="text-center text-[11px] text-white/40" data-testid="pay-info-cash-only">
                  {t('payWithCashStaff')}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
